import styles from "@/overlay/components/QuickAccessLabel/QuickAccessLabel.module.scss";
import { classNames } from "@/utility/jsx";
import { convertToCSSProperties } from "@/utility/style";
import type { ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import { type ReactNode, type Ref, forwardRef } from "react";

type LabelIconProps = {
  name: string;
  titleStyle?: ElementStyle;
  /** Uses the "material-symbols-outlined" font instead of "material-icons". */
  outlined?: boolean;
};

/** Material icon sized like the label text. */
export function LabelIcon({ name, titleStyle, outlined = false }: LabelIconProps) {
  return (
    <span
      className={outlined ? "material-symbols-outlined" : "material-icons"}
      style={{ fontSize: titleStyle?.fontSize || "1rem" }}
    >
      {name}
    </span>
  );
}

/** Text styled with the label's title style. */
export function LabelText({ titleStyle, children }: { titleStyle?: ElementStyle; children: ReactNode }) {
  return <div style={convertToCSSProperties(titleStyle)}>{children}</div>;
}

type LabelChipProps = {
  children: ReactNode;
  onClick?: () => void;
  /** Blocks interaction. The chip stays visible. */
  disabled?: boolean;
  /** `default`: icon + text. `icon`: compact, for a single icon. `static`: display only, no hover/click. */
  variant?: "default" | "icon" | "static";
  /** The chip's window/popover is currently open. */
  active?: boolean;
  className?: string;
};

/** A rounded item in the label's main row. */
export const LabelChip = forwardRef<HTMLDivElement, LabelChipProps>(function LabelChip(
  { children, onClick, disabled = false, variant = "default", active = false, className },
  ref: Ref<HTMLDivElement>,
) {
  const isStatic = variant === "static";

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div
      ref={ref}
      className={classNames(
        styles["label-row-item"],
        variant === "icon" ? styles["label-row-item--icon"] : "",
        isStatic ? styles["label-row-item--static"] : "",
        active ? styles["label-row-item--active"] : "",
        className,
      )}
      onClick={disabled || isStatic ? undefined : onClick}
      style={disabled || isStatic ? { pointerEvents: "none" } : undefined}
    >
      {children}
    </div>
  );
});
